import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import './MarketingHubModal.css';
import BlogManager from './BlogManager.js';
import {
  AssetsPanel,
  AssetPreviewModal,
  mktFetch,
  MARKETING_API,
} from './MarketingHubModal';
import MarketingPlatformPreview, { assetUrl } from './MarketingPlatformPreview.js';
import MarketingGraphicPromptModal from './MarketingGraphicPromptModal.js';
import MarketingVideoPromptModal from './MarketingVideoPromptModal.js';
import MarketingPublishModal from './MarketingPublishModal.js';
import MarketingDraftFeedback from './MarketingDraftFeedback.js';
import MarketingAssetVersions from './MarketingAssetVersions.js';

const API = MARKETING_API;

function platformLabel(platform) {
  if (platform === 'opo_news') return 'OPO NEWS';
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'tiktok') return 'TikTok';
  return platform || 'channel';
}

function WriteContentModal({ narrative, channels, onClose, onDone, onNavigate }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const identityChannels = useMemo(
    () => channels.filter((c) => c.brand_id === narrative.brand_id),
    [channels, narrative.brand_id]
  );

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (!selected.length) {
      setMsg('Select at least one channel — each platform gets its own draft.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/generate`, {
        method: 'POST',
        body: JSON.stringify({ narrative_id: narrative.id, channel_ids: selected.map(Number) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Generate failed');
      if (d.started && d.run_id) {
        setMsg(d.message || 'Writing…');
        const result = await pollWriterRun(d.run_id, narrative.id, ({ elapsed, draftCount, model }) => {
          setMsg(`Writing… ${elapsed}s · ${draftCount} draft(s) · ${model || d.model || 'Ollama'}`);
        });
        setMsg(`Created ${result.created || selected.length} draft(s). Trend removed from this list.`);
      } else {
        setMsg(`Created ${d.created || selected.length} draft(s). Trend removed from this list.`);
      }
      await onDone?.();
      onNavigate?.('studio');
      onClose();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mkt-publish-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mkt-publish-modal mkt-write-content-modal"
        role="dialog"
        aria-labelledby="write-content-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mkt-publish-head">
          <div className="mkt-publish-head-titles">
            <h3 id="write-content-title">WRITE CONTENT</h3>
            <span className="mkt-publish-sub">{narrative.brand_name || 'Identity'} · choose channels</span>
          </div>
          <button type="button" className="mkt-publish-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mkt-write-content-body">
          <p className="marketing-source-topic" style={{ marginTop: 0 }}>{narrative.title}</p>
          <p className="marketing-panel-desc">
            Pick which channels should get a draft for this trend. One draft per channel — LinkedIn and TikTok are separate.
            After writing, this trend leaves Trends and appears only in Studio + Review.
          </p>
          {identityChannels.length === 0 ? (
            <p className="marketing-msg-err">No channels for {narrative.brand_name}. Add channels under Setup → Channels first.</p>
          ) : (
            <div className="marketing-channel-checks mkt-write-channel-checks">
              {identityChannels.map((ch) => (
                <label key={ch.id} className="marketing-check-label">
                  <input
                    type="checkbox"
                    checked={selected.includes(ch.id)}
                    disabled={busy}
                    onChange={() => toggle(ch.id)}
                  />
                  <span>
                    <strong>{ch.display_name || ch.brand_name}</strong>
                    {' · '}
                    {platformLabel(ch.platform)}
                    {ch.voice_profile_name ? ` · ${ch.voice_profile_name}` : ''}
                  </span>
                </label>
              ))}
            </div>
          )}
          {msg && <p className={msg.toLowerCase().includes('fail') || msg.includes('Select') ? 'marketing-msg-err' : 'marketing-msg'}>{msg}</p>}
          <div className="marketing-form-actions">
            <button type="button" className="blog-tactical-action-btn" disabled={busy} onClick={onClose}>CANCEL</button>
            <button
              type="button"
              className="blog-tactical-action-btn blog-tactical-action-edit"
              disabled={busy || !identityChannels.length}
              onClick={submit}
            >
              {busy ? 'WRITING…' : `WRITE ${selected.length || ''} DRAFT${selected.length === 1 ? '' : 'S'}`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

async function dismissNarrative(id) {
  if (!window.confirm('Remove this trend from the list? Any drafts already created from it will stay in Studio + Review.')) return false;
  const res = await mktFetch(`${API}/platform/narratives/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    window.alert(d.error || 'Could not remove trend.');
    return false;
  }
  return true;
}

async function pollWriterRun(runId, narrativeId, onProgress) {
  for (let i = 0; i < 72; i++) {
    await new Promise((r) => setTimeout(r, 5000));
    const runRes = await mktFetch(`${API}/platform/agents/runs/${runId}`);
    const runData = await runRes.json();
    const run = runData.run;
    let draftCount = 0;
    if (narrativeId) {
      const dRes = await mktFetch(`${API}/platform/drafts?narrative_id=${narrativeId}&limit=20`);
      const dData = await dRes.json();
      draftCount = (dData.drafts || []).length;
    }
    onProgress?.({
      elapsed: (i + 1) * 5,
      draftCount,
      model: run?.stats?.model,
      status: run?.status,
    });
    if (run?.status === 'completed') {
      const stats = typeof run.stats === 'string' ? JSON.parse(run.stats) : (run.stats || {});
      return { ok: true, created: stats.created ?? draftCount, model: stats.model };
    }
    if (run?.status === 'failed') {
      throw new Error(run.error_message || 'Writer failed');
    }
  }
  throw new Error('Writer still running — check Studio + Review in a minute.');
}

async function pollGraphicRun(runId, onProgress) {
  for (let i = 0; i < 120; i++) {
    await new Promise((r) => setTimeout(r, 5000));
    const runRes = await mktFetch(`${API}/platform/agents/runs/${runId}`);
    const runData = await runRes.json().catch(() => ({}));
    const run = runData.run;
    const stats = typeof run?.stats === 'string' ? JSON.parse(run.stats) : (run?.stats || {});
    onProgress?.({ elapsed: (i + 1) * 5, engine: stats.engine, status: run?.status });
    if (run?.status === 'completed') return stats;
    if (run?.status === 'failed') throw new Error(run.error_message || 'Media generation failed');
  }
  throw new Error('Still generating — check Studio + Review in a minute.');
}

function MarketingSyncButton({ busy, onClick, className = '' }) {
  return (
    <button
      type="button"
      className={`opo-refresh-btn${busy ? ' is-busy' : ''} ${className}`.trim()}
      disabled={busy}
      title="Reload dashboard stats from the database (instant — does not run the scout)"
      onClick={onClick}
    >
      <span className={`opo-refresh-icon${busy ? ' opo-refresh-spin' : ''}`} aria-hidden />
      {busy ? 'SYNCING…' : 'SYNC'}
    </button>
  );
}

function MarketingScoutButton({ busy, onClick, className = '', label }) {
  return (
    <button
      type="button"
      className={`opo-refresh-btn marketing-scout-hero-btn${busy ? ' is-busy is-scout-running' : ''} ${className}`.trim()}
      disabled={busy}
      title="Read active sources, detect trends, and create narratives (~1–3 min)"
      onClick={onClick}
    >
      <span className={`opo-refresh-icon${busy ? ' opo-refresh-spin opo-scout-spin' : ''}`} aria-hidden />
      {busy ? (label || 'SCOUT RUNNING…') : 'RUN SCOUT'}
    </button>
  );
}

// Two-phase UX: Setup (configure once) → Operations (daily content loop)
const TAB_GROUPS = [
  {
    id: 'operations',
    label: 'Operations',
    hint: 'Run the content loop',
    tabs: [
      { id: 'overview', label: 'Overview', icon: 'fas fa-satellite-dish' },
      { id: 'trends', label: 'Trends', icon: 'fas fa-fire' },
      { id: 'studio', label: 'Studio + Review', icon: 'fas fa-pen-fancy' },
      { id: 'schedules', label: 'Schedules', icon: 'fas fa-clock' },
      { id: 'analytics', label: 'Analytics', icon: 'fas fa-chart-bar' },
      { id: 'blog-management', label: 'Blog Management', icon: 'fas fa-blog' },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    hint: 'Configure compliance, identities, sources, voices & channels',
    tabs: [
      { id: 'compliance', label: 'Compliance Library', icon: 'fas fa-shield-alt' },
      { id: 'identities', label: 'Identities', icon: 'fas fa-fingerprint' },
      { id: 'sources', label: 'Sources', icon: 'fas fa-rss' },
      { id: 'voice-profiles', label: 'Voice Profiles', icon: 'fas fa-microphone' },
      { id: 'channels', label: 'Channels', icon: 'fas fa-sitemap' },
    ],
  },
];

const ALL_TABS = TAB_GROUPS.flatMap((g) => g.tabs.map((t) => ({ ...t, group: g.id })));

function runLevelLabel(level) {
  return {
    good: 'SCOUT PIPELINE READY',
    warning: 'SCOUT NEEDS ATTENTION',
    critical: 'SCOUT OFFLINE',
    unknown: 'SCOUT STATUS UNKNOWN',
  }[level] || 'SCOUT STATUS UNKNOWN';
}

function PostizBanner({ postiz, compact }) {
  if (!postiz) return null;
  const needsSetup = !postiz.reachable || !postiz.configured || postiz.localhost_public;
  if (!needsSetup) return null;

  let level = 'warning';
  let title = 'POSTIZ NOT CONFIGURED';
  let detail = 'Social distribution requires a running Postiz instance with a public URL and API key.';
  if (!postiz.reachable) {
    level = 'critical';
    title = 'POSTIZ UNREACHABLE';
    detail = `Server cannot reach Postiz at ${postiz.url}. Run: cd node-postiz && docker compose up -d`;
  } else if (postiz.localhost_public) {
    level = 'critical';
    title = 'POSTIZ URL IS LOCALHOST';
    detail = 'Set POSTIZ_PUBLIC_URL to a browser-reachable URL (e.g. https://postiz.optiprime.ai). localhost only works on the server itself.';
  } else if (!postiz.configured) {
    detail = 'Postiz is running. Add POSTIZ_API_KEY from Postiz Settings → Public API to .env.';
  }

  return (
    <div className={`marketing-postiz-banner marketing-postiz-${level}${compact ? ' marketing-postiz-compact' : ''}`}>
      <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
      <div className="marketing-postiz-banner-body">
        <strong>{title}</strong>
        <p>{detail}</p>
        {postiz.public_url && postiz.reachable && (
          <a
            href={postiz.public_url}
            target="_blank"
            rel="noopener noreferrer"
            className="blog-tactical-action-btn blog-tactical-action-view"
          >
            {postiz.localhost_public ? 'OPEN POSTIZ (SERVER ONLY)' : 'OPEN POSTIZ ADMIN'}
          </a>
        )}
      </div>
    </div>
  );
}

function sourceDisplayParts(url) {
  try {
    const u = new URL(url);
    return {
      host: u.hostname.replace(/^www\./, ''),
      path: u.pathname === '/' ? '' : u.pathname,
      full: url,
    };
  } catch {
    return { host: url, path: '', full: url };
  }
}

function SourcesPanel({ brands, onChanged }) {
  const [sources, setSources] = useState([]);
  const [filterBrand, setFilterBrand] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    brand_id: '', url: '', label: '', keywords: '',
  });

  const brandById = useMemo(() => {
    const map = new Map();
    (brands || []).forEach((b) => map.set(b.id, b));
    return map;
  }, [brands]);

  const load = useCallback(() => {
    const q = filterBrand ? `?brand_id=${filterBrand}` : '';
    mktFetch(`${API}/platform/sources${q}`)
      .then((r) => r.json())
      .then((d) => setSources((d.sources || []).filter((s) => s.url)))
      .catch(() => {});
  }, [filterBrand]);

  useEffect(() => { load(); }, [load]);

  // When filtering by identity, pre-fill the add form so you don't pick twice
  useEffect(() => {
    setForm((v) => ({ ...v, brand_id: filterBrand || v.brand_id }));
  }, [filterBrand]);

  const saveSource = async () => {
    setMsg('');
    const brandId = form.brand_id || filterBrand;
    if (!brandId) { setMsg('Select an identity first.'); return; }
    if (!form.url) { setMsg('Website URL is required.'); return; }
    const res = await mktFetch(`${API}/platform/sources`, {
      method: 'POST',
      body: JSON.stringify({
        brand_id: brandId,
        url: form.url,
        topic: form.label || null,
        keywords: form.keywords ? form.keywords.split(',').map((k) => k.trim()).filter(Boolean) : [],
      }),
    });
    const data = await res.json();
    if (!res.ok) { setMsg(data.error || 'Save failed'); return; }
    setMsg('Website added — editorial scout will scrape it on the next run.');
    setForm({ brand_id: filterBrand || '', url: '', label: '', keywords: '' });
    load();
    onChanged?.();
  };

  const toggleActive = async (src) => {
    await mktFetch(`${API}/platform/sources/${src.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !src.active }),
    });
    load();
    onChanged?.();
  };

  const removeSource = async (id) => {
    if (!window.confirm('Remove this website from editorial sources?')) return;
    await mktFetch(`${API}/platform/sources/${id}`, { method: 'DELETE' });
    load();
    onChanged?.();
  };

  const activeCount = sources.filter((s) => s.active).length;
  const pausedCount = sources.length - activeCount;
  const companiesCovered = new Set(sources.map((s) => s.brand_id)).size;

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">
        <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
        EDITORIAL WEBSITES
        <span className="blog-tactical-list-count opo-mono-value">{sources.length}</span>
      </h3>
      <p className="marketing-panel-desc">
        Each card is a <strong>website</strong> linked to an <strong>identity</strong>. When you run <strong>Editorial Scout</strong> (Overview → Run Scout),
        the system scrapes <em>all active sources</em> for that identity, compares headlines across them, researches the top stories on the web,
        then creates trends in the Trends tab. Use Studio + Review to write posts from those trends using your rule-set.
      </p>

      <div className="marketing-filter-bar">
        <span className="users-filter-tag">VIEW &amp; ADD FOR</span>
        <label>
          Identity
          <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
            <option value="">All identities</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <button type="button" className="btn btn-secondary" onClick={() => setFilterBrand('')}>Clear</button>
      </div>

      <div className="opo-kpi-row marketing-sources-kpi">
        <div className="opo-kpi">
          <div className="opo-kpi-label">WEBSITES</div>
          <div className="opo-kpi-value opo-mono-value">{sources.length}</div>
          <div className="opo-kpi-foot">URLs linked to companies</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">ACTIVE</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${activeCount > 0 ? 'good' : 'warning'}`}>{activeCount}</div>
          <div className="opo-kpi-foot">scraped each scout run</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">PAUSED</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${pausedCount > 0 ? 'warning' : 'good'}`}>{pausedCount}</div>
          <div className="opo-kpi-foot">skipped until re-enabled</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">COMPANIES</div>
          <div className="opo-kpi-value opo-mono-value">{companiesCovered}</div>
          <div className="opo-kpi-foot">with at least one website</div>
        </div>
      </div>

      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="opo-stat-header">
          <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
          <span className="opo-service-name">ADD WEBSITE</span>
        </div>
        <div className="marketing-form-grid">
          {filterBrand ? (
            <label className="marketing-form-full">Adding website for
              <input readOnly className="marketing-readonly-field" value={brands.find((b) => String(b.id) === String(filterBrand))?.name || 'Selected identity'} />
            </label>
          ) : (
            <label>Identity
              <select value={form.brand_id} onChange={(e) => setForm((v) => ({ ...v, brand_id: e.target.value }))}>
                <option value="">Select identity</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className="marketing-form-full">Website URL
            <input value={form.url} onChange={(e) => setForm((v) => ({ ...v, url: e.target.value }))} placeholder="https://www.rew.ca/articles" />
          </label>
          <label>Label (optional)
            <input value={form.label} onChange={(e) => setForm((v) => ({ ...v, label: e.target.value }))} placeholder="e.g. BC real estate news" />
          </label>
          <label>Keywords (optional)
            <input value={form.keywords} onChange={(e) => setForm((v) => ({ ...v, keywords: e.target.value }))} placeholder="Vancouver, brokerage, RESA" />
          </label>
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={saveSource}>SAVE WEBSITE</button>
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>

      {sources.length === 0 && (
        <div className="blog-tactical-empty">NO WEBSITES LINKED — ADD A URL ABOVE FOR EACH COMPANY</div>
      )}
      <div className="marketing-source-list">
        {sources.map((src) => {
          const level = src.active ? 'good' : 'unknown';
          const { host, path, full } = sourceDisplayParts(src.url);
          const linkedBrand = brandById.get(src.brand_id);
          const brandLogoUrl = src.brand_logo_url || linkedBrand?.logo_url;
          return (
            <div key={src.id} className={`opo-card-tactical opo-card-${level} marketing-source-card`}>
              <div className="opo-stat-header marketing-source-card-head">
                <div className="marketing-source-title-block">
                  <div className="marketing-source-title-row">
                    <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
                    <div className="marketing-source-title-text">
                      <a
                        href={full}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="marketing-source-primary-title"
                        title={full}
                      >
                        {host}
                      </a>
                      {path && <span className="marketing-source-path opo-mono-value">{path}</span>}
                    </div>
                  </div>
                  <p className="marketing-source-identity-line">
                    <span className="marketing-source-identity-label">Linked to</span>
                    <span className="marketing-source-identity-chip">
                      {brandLogoUrl && src.brand_id && (
                        <img
                          src={`${API}/platform/identities/${src.brand_id}/logo/file?v=${encodeURIComponent(brandLogoUrl)}`}
                          alt=""
                          className="marketing-source-identity-logo"
                        />
                      )}
                      <span className="marketing-source-identity-name">{src.brand_name || 'Unassigned identity'}</span>
                    </span>
                  </p>
                </div>
                <span className={`widgets-status-badge widgets-status-${level}`}>
                  {src.active ? 'ACTIVE' : 'PAUSED'}
                </span>
              </div>
              {src.topic && <p className="marketing-source-topic">{src.topic}</p>}
              {src.keywords?.length > 0 && (
                <p className="marketing-source-kw">{src.keywords.join(' · ')}</p>
              )}
              <p className="marketing-source-meta">
                {src.last_fetched_at
                  ? `Last scraped: ${new Date(src.last_fetched_at).toLocaleString()}`
                  : 'Not scraped yet — run Editorial Scout from Overview or add a Schedule'}
              </p>
              <div className="marketing-form-actions">
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => toggleActive(src)}>
                  {src.active ? 'PAUSE' : 'RESUME'}
                </button>
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={() => removeSource(src.id)}>DELETE</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewPanel({ onNavigate, overview, onSync, syncBusy, scoutBusy, scoutMsg, onRunScout, onDismissNarrative }) {
  const narratives = (overview?.narratives || []).slice(0, 3);
  const runs = (overview?.recent_runs || []).slice(0, 5);
  const lastRun = overview?.last_editorial_run;
  const scoutRunning = lastRun?.status === 'running' || scoutBusy;

  return (
    <div className="marketing-tactical-panel">
      <PostizBanner postiz={overview?.postiz} />

      {(scoutRunning || scoutMsg) && (
        <div className={`marketing-postiz-banner marketing-postiz-${scoutRunning ? 'warning' : 'good'} marketing-scout-status`}>
          <span className={`opo-pip opo-pip-${scoutRunning ? 'warning' : 'good'}`}><span className="opo-pip-core" /></span>
          <div className="marketing-postiz-banner-body">
            <strong>{scoutRunning ? 'EDITORIAL SCOUT IN PROGRESS' : 'EDITORIAL SCOUT'}</strong>
            <p>{scoutMsg || 'Reading sources and analyzing trends with Ollama — usually takes 1–3 minutes. This page refreshes automatically.'}</p>
          </div>
        </div>
      )}

      <div className="marketing-section-split">
        <div className="opo-card-tactical opo-card-good">
          <h3 className="opo-section-heading">▮ TODAY&apos;S NARRATIVES</h3>
          {narratives.length === 0 && (
            <div className="blog-tactical-empty">
              NO NARRATIVES YET — CONFIGURE SOURCES, THEN RUN EDITORIAL SCOUT
            </div>
          )}
          <ul className="marketing-narrative-list">
            {narratives.map((n) => (
              <li key={n.id} className="marketing-narrative-item">
                <strong>{n.title}</strong>
                <p>{n.summary}</p>
                <span className="marketing-source-meta">{n.brand_name}{n.pattern_strength ? ` · strength ${n.pattern_strength}` : ''}</span>
                <div className="marketing-form-actions marketing-trend-actions">
                  <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => onNavigate('trends')}>
                    CREATE CONTENT
                  </button>
                  <button type="button" className="marketing-trend-dismiss" title="Dismiss trend" aria-label="Dismiss trend" onClick={() => onDismissNarrative(n.id)}>
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="opo-card-tactical opo-card-good">
          <h3 className="opo-section-heading">▮ AGENT RUNS</h3>
          {lastRun && (
            <div className="marketing-last-run">
              <span className={`widgets-status-badge widgets-status-${lastRun.status === 'completed' ? 'good' : lastRun.status === 'failed' ? 'critical' : 'warning'}`}>
                LAST SCOUT: {lastRun.status?.toUpperCase()}
              </span>
              <span className="marketing-source-meta">
                {lastRun.started_at ? new Date(lastRun.started_at).toLocaleString() : '—'}
                {lastRun.finished_at ? ` → ${new Date(lastRun.finished_at).toLocaleString()}` : ''}
              </span>
              {lastRun.error_message && <p className="marketing-msg-err">{lastRun.error_message}</p>}
              {lastRun.stats && (
                <p className="marketing-source-meta">
                  Articles: {lastRun.stats.articlesIngested ?? lastRun.stats.articles_fetched ?? '—'} · Narratives: {lastRun.stats.narrativesCreated ?? lastRun.stats.narratives_created ?? '—'}
                </p>
              )}
            </div>
          )}
          {runs.length === 0 && !lastRun && (
            <div className="blog-tactical-empty">NO AGENT RUNS LOGGED — ADD A SCHEDULE OR RUN THE SCOUT MANUALLY</div>
          )}
          <ul className="marketing-run-list">
            {runs.map((r) => (
              <li key={r.id} className="marketing-run-item">
                <span className={`widgets-status-badge widgets-status-${r.status === 'completed' ? 'good' : r.status === 'failed' ? 'critical' : 'warning'}`}>
                  {r.agent_type}
                </span>
                <span className="opo-mono-value">{r.status}</span>
                <span className="marketing-source-meta">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="marketing-form-actions marketing-overview-actions">
        <MarketingScoutButton busy={scoutBusy} onClick={onRunScout} label="SCOUT RUNNING…" />
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={scoutBusy} onClick={() => onNavigate('identities')}>IDENTITIES</button>
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={scoutBusy} onClick={() => onNavigate('sources')}>MANAGE SOURCES</button>
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={scoutBusy} onClick={() => onNavigate('trends')}>TRENDS</button>
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={scoutBusy} onClick={() => onNavigate('studio')}>STUDIO + REVIEW</button>
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={scoutBusy} onClick={() => onNavigate('channels')}>CONNECT CHANNELS</button>
        <MarketingSyncButton busy={syncBusy} onClick={onSync} />
      </div>
    </div>
  );
}

function ChannelsPanel({ postiz, onSync, syncBusy: parentSyncBusy }) {
  const [brands, setBrands] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [panelSyncBusy, setPanelSyncBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [connectBusyId, setConnectBusyId] = useState(null);
  const [connectErrByChannel, setConnectErrByChannel] = useState({});
  const [form, setForm] = useState({
    brand_id: '', voice_profile_id: '', platform: 'linkedin', display_name: '', voice_guide: '',
  });

  const load = useCallback(() => {
    Promise.all([
      mktFetch(`${API}/platform/identities`).then((r) => r.json()),
      mktFetch(`${API}/platform/voice-profiles`).then((r) => r.json()),
      mktFetch(`${API}/platform/channels`).then((r) => r.json()),
    ]).then(([b, p, c]) => {
      setBrands(b.identities || []);
      setVoiceProfiles(p.voice_profiles || []);
      setChannels(c.channels || []);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePanelSync = async () => {
    if (panelSyncBusy || parentSyncBusy) return;
    setPanelSyncBusy(true);
    try {
      await load();
      await onSync?.();
    } finally {
      setPanelSyncBusy(false);
    }
  };

  const syncBusy = panelSyncBusy || parentSyncBusy;

  const saveChannel = async () => {
    setMsg('');
    const res = await mktFetch(`${API}/platform/channels`, {
      method: 'POST',
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (!res.ok) { setMsg(data.error || 'Save failed'); return; }
    setMsg('Channel saved.');
    load();
    onSync?.();
  };

  const connectOAuth = async (channelId) => {
    setMsg('');
    setConnectErrByChannel((prev) => { const n = { ...prev }; delete n[channelId]; return n; });
    setConnectBusyId(channelId);
    try {
      const res = await mktFetch(`${API}/platform/channels/${channelId}/connect`, { method: 'POST' });
      const data = await res.json();
      const detail = [data.error, data.message].filter(Boolean).join(' — ');

      if (data.oauth_url) {
        const win = window.open(data.oauth_url, '_blank', 'noopener,noreferrer');
        if (!win) {
          const blocked = `Popup blocked. Allow popups for this site, or open manually:\n${data.oauth_url}`;
          setConnectErrByChannel((prev) => ({ ...prev, [channelId]: blocked }));
          setMsg(blocked);
        } else {
          setMsg(detail || 'OAuth opened in a new tab — complete login in Postiz, then paste the integration ID on this card.');
        }
      } else {
        const errText = detail || 'Connect failed — Postiz is not ready for browser OAuth yet.';
        setConnectErrByChannel((prev) => ({ ...prev, [channelId]: errText }));
        setMsg(errText);
      }
      load();
    } catch (e) {
      setConnectErrByChannel((prev) => ({ ...prev, [channelId]: e.message }));
      setMsg(e.message);
    } finally {
      setConnectBusyId(null);
    }
  };

  const saveIntegrationId = async (ch, integrationId) => {
    await mktFetch(`${API}/platform/channels/${ch.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        postiz_integration_id: integrationId,
        connection_status: integrationId ? 'connected' : 'pending',
      }),
    });
    load();
    onSync?.();
  };

  const deleteChannel = async (ch) => {
    if (!window.confirm(`Remove channel "${ch.display_name || ch.brand_name}"? Existing drafts are kept.`)) return;
    setMsg('');
    const res = await mktFetch(`${API}/platform/channels/${ch.id}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) { setMsg(data.error || 'Delete failed'); return; }
    setMsg('Channel removed.');
    load();
    onSync?.();
  };

  const connected = channels.filter((c) => c.connection_status === 'connected' || c.platform === 'opo_news').length;
  const pending = channels.length - connected;

  return (
    <div className="marketing-tactical-panel">
      <PostizBanner postiz={postiz} />
      <p className="marketing-panel-desc">
        Each channel connects an identity + voice profile to a platform. Create voices in <strong>Voice Profiles</strong> first, then link one here.
        Social channels distribute via Postiz; <strong>OPO NEWS</strong> publishes to optiprime.ai and AI Navigation.
      </p>

      <div className="marketing-filter-bar" style={{ marginBottom: 16 }}>
        <MarketingSyncButton busy={syncBusy} onClick={handlePanelSync} />
      </div>

      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="opo-stat-header">
          <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
          <span className="opo-service-name">ADD CHANNEL</span>
        </div>
        <div className="marketing-form-grid">
          <label>Identity
            <select value={form.brand_id} onChange={(e) => setForm((v) => ({ ...v, brand_id: e.target.value }))}>
              <option value="">Select identity</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label>Voice profile
            <select value={form.voice_profile_id} onChange={(e) => setForm((v) => ({ ...v, voice_profile_id: e.target.value }))}>
              <option value="">Select voice (optional)</option>
              {voiceProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {!voiceProfiles.length && (
              <span className="marketing-field-hint">No voices yet — add one on the Voice Profiles tab.</span>
            )}
          </label>
          <label>Platform
            <select value={form.platform} onChange={(e) => setForm((v) => ({ ...v, platform: e.target.value }))}>
              <option value="opo_news">OPO NEWS</option>
              <option value="linkedin">LinkedIn</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="x">X / Twitter</option>
            </select>
          </label>
          <label>Display name
            <input value={form.display_name} onChange={(e) => setForm((v) => ({ ...v, display_name: e.target.value }))} placeholder="Frederik @ Optimum Realty" />
          </label>
          <label className="marketing-form-full">Voice guide
            <textarea rows={2} value={form.voice_guide} onChange={(e) => setForm((v) => ({ ...v, voice_guide: e.target.value }))} placeholder="Tone, audience, compliance notes…" />
          </label>
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={saveChannel}>SAVE CHANNEL</button>
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>

      <h3 className="opo-section-heading">
        <span className="opo-pip opo-pip-warning"><span className="opo-pip-core" /></span>
        CHANNELS
        <span className="blog-tactical-list-count opo-mono-value">{channels.length}</span>
      </h3>
      <p className="marketing-source-meta">{connected} connected · {pending} pending</p>

      <div className="marketing-channel-grid">
        {channels.map((ch) => {
          const isOpoNews = ch.platform === 'opo_news';
          const level = isOpoNews || ch.connection_status === 'connected' ? 'good' : 'warning';
          return (
            <div key={ch.id} className={`opo-card-tactical opo-card-${level} marketing-channel-card`}>
              <div className="opo-stat-header">
                <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
                <span className="opo-service-name">{ch.display_name || `${ch.brand_name} · ${isOpoNews ? 'OPO NEWS' : ch.platform}`}</span>
                <span className={`widgets-status-badge widgets-status-${level}`}>{isOpoNews ? 'internal' : (ch.connection_status || 'pending')}</span>
              </div>
              <p className="marketing-source-meta">{ch.brand_name} · {ch.voice_profile_name || ch.persona_name || 'no voice'} · {isOpoNews ? 'OPO NEWS' : ch.platform}</p>
              {isOpoNews ? (
                <p className="marketing-source-topic">Internal news channel — publish from Studio to optiprime.ai and AI Navigation. No Postiz required.</p>
              ) : (
              <>
              {ch.voice_guide && <p className="marketing-source-topic">{ch.voice_guide.slice(0, 120)}{ch.voice_guide.length > 120 ? '…' : ''}</p>}
              <label className="marketing-integration-label">Postiz integration ID
                <input
                  className="opo-mono-value"
                  defaultValue={ch.postiz_integration_id || ''}
                  placeholder="From Postiz after OAuth"
                  onBlur={(e) => {
                    if (e.target.value !== (ch.postiz_integration_id || '')) {
                      saveIntegrationId(ch, e.target.value.trim());
                    }
                  }}
                />
              </label>
              <div className="marketing-form-actions">
                <button
                  type="button"
                  className="blog-tactical-action-btn blog-tactical-action-edit"
                  disabled={connectBusyId === ch.id}
                  onClick={() => connectOAuth(ch.id)}
                >
                  {connectBusyId === ch.id ? 'CONNECTING…' : (ch.connection_status === 'connected' ? 'RECONNECT' : 'CONNECT VIA POSTIZ')}
                </button>
              </div>
              {connectErrByChannel[ch.id] && (
                <p className="marketing-msg-err marketing-channel-connect-err">{connectErrByChannel[ch.id]}</p>
              )}
              </>
              )}
              <div className="marketing-form-actions marketing-trend-actions">
                <button
                  type="button"
                  className="marketing-trend-dismiss"
                  title="Remove channel"
                  aria-label="Remove channel"
                  onClick={() => deleteChannel(ch)}
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {msg && <p className={msg.toLowerCase().includes('fail') || msg.includes('localhost') || msg.includes('POSTIZ') ? 'marketing-msg-err' : 'marketing-msg'} style={{ marginTop: 12 }}>{msg}</p>}
    </div>
  );
}

function StudioReviewPanel({ onPreviewAsset, identities, selectedIdentity, onSelectIdentity }) {
  const [narratives, setNarratives] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedNarrative, setSelectedNarrative] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [previewDraft, setPreviewDraft] = useState(null);
  const [graphicPromptDraft, setGraphicPromptDraft] = useState(null);
  const [videoPromptDraft, setVideoPromptDraft] = useState(null);
  const [publishComposerDraft, setPublishComposerDraft] = useState(null);

  const idQ = selectedIdentity ? `&identity_id=${selectedIdentity}` : '';
  const load = useCallback(() => {
    Promise.all([
      mktFetch(`${API}/platform/narratives?limit=30${idQ}`).then((r) => r.json()),
      mktFetch(`${API}/platform/drafts?limit=80${idQ}`).then((r) => r.json()),
      mktFetch(`${API}/platform/channels?x=1${idQ}`).then((r) => r.json()),
    ]).then(([n, d, c]) => {
      setNarratives(n.narratives || []);
      setDrafts(d.drafts || []);
      setChannels(c.channels || []);
    });
  }, [idQ]);

  useEffect(() => { load(); }, [load]);

  const toggleChannel = (id) => {
    setSelectedChannels((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const generateDrafts = async () => {
    if (!selectedNarrative) { setMsg('Select a trend/narrative first.'); return; }
    if (!selectedChannels.length) { setMsg('Select at least one channel.'); return; }
    setBusy(true); setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/generate`, {
        method: 'POST',
        body: JSON.stringify({
          narrative_id: Number(selectedNarrative),
          channel_ids: selectedChannels.map(Number),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generate failed');
      if (data.started && data.run_id) {
        setMsg(`${data.message || 'Writing…'} (${data.model || 'Ollama'})`);
        const result = await pollWriterRun(data.run_id, Number(selectedNarrative), ({ elapsed, draftCount, model }) => {
          setMsg(`Qwen writing… ${elapsed}s · ${draftCount} draft(s) so far · ${model || data.model || 'Ollama'}`);
        });
        setMsg(`Generated ${result.created || 0} draft(s) — now in the review queue.`);
      } else {
        setMsg(`Generated ${data.created || 0} draft(s) — now in the review queue.`);
      }
      load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const patchDraft = async (id, patch) => {
    await mktFetch(`${API}/platform/drafts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    load();
  };

  const reviewAction = async (id, action, body) => {
    setBusy(true); setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${id}/${action}`, { method: 'POST', body: JSON.stringify(body || {}) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `${action} failed`);
      setMsg(`Draft ${action}ed.`);
      load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const saveEdit = async (d) => {
    const e = edits[d.id];
    if (!e) return;
    await patchDraft(d.id, { title: e.title, body: e.body });
    setEdits((prev) => { const n = { ...prev }; delete n[d.id]; return n; });
    setMsg('Edits saved.');
  };

  const generateMedia = async (draftId, kind) => {
    setBusy(true); setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draftId}/generate-media`, { method: 'POST', body: JSON.stringify({ kind }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Media generation failed');
      let result = data;
      if (data.started && data.run_id) {
        setMsg(data.message || 'Generating media…');
        result = await pollGraphicRun(data.run_id, ({ elapsed, engine }) => {
          setMsg(`Generating… ${elapsed}s${engine ? ` · ${engine}` : ''}`);
        });
      }
      const engine = result.engine ? ` (${result.engine})` : '';
      const mode = result.preview_mode ? ` · ${result.preview_mode}` : '';
      setMsg(`Media generated${engine}${mode}: ${result.asset?.filename || kind}`);
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const reviewQueue = drafts.filter((d) => d.status === 'in_review' || d.status === 'draft');
  const approved = drafts.filter((d) => d.status === 'approved' || d.status === 'scheduled');
  const done = drafts.filter((d) => d.status === 'posted' || d.status === 'rejected' || d.status === 'failed');

  const draftCard = (d, showReview) => {
    const e = edits[d.id] || { title: d.title || '', body: d.body || '' };
    const level = d.status === 'posted' ? 'good' : (d.status === 'failed' || d.status === 'rejected') ? 'critical' : 'warning';
    const thumbSrc = d.asset_id ? assetUrl(d.asset_id) : null;
    const meta = typeof d.metadata === 'string' ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })() : (d.metadata || {});
    const graphicMeta = meta.graphic;
    const videoMeta = meta.video;
    const assetIsVideo = d.asset_kind === 'video';
    const formatBadge = d.platform === 'linkedin' && (d.format === 'article' || d.format === 'longform')
      ? 'article' : (d.format || d.platform);
    return (
      <div key={d.id} className={`opo-card-tactical opo-card-${level} marketing-review-card`}>
        <div className="opo-stat-header">
          <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
          <span className="opo-service-name">{d.brand_name} · {d.platform} · {formatBadge}</span>
          <span className={`widgets-status-badge widgets-status-${level}`}>{d.status}</span>
        </div>
        {showReview ? (
          <>
            {thumbSrc && (
              <div className="marketing-draft-graphic-preview">
                {assetIsVideo ? (
                  <video src={thumbSrc} controls muted playsInline style={{ width: '100%', display: 'block' }} />
                ) : (
                  <img src={thumbSrc} alt="Generated graphic" />
                )}
                {(assetIsVideo ? videoMeta?.engine : graphicMeta?.engine) && (
                  <span className="marketing-draft-graphic-badge">{assetIsVideo ? videoMeta.engine : graphicMeta.engine}</span>
                )}
              </div>
            )}
            {d.asset_id && <MarketingAssetVersions draftId={d.id} onChange={load} />}
            <label className="marketing-integration-label">Title
              <input value={e.title} onChange={(ev) => setEdits((p) => ({ ...p, [d.id]: { ...e, title: ev.target.value } }))} />
            </label>
            <label className="marketing-integration-label">Body
              <textarea rows={4} value={e.body} onChange={(ev) => setEdits((p) => ({ ...p, [d.id]: { ...e, body: ev.target.value } }))} />
            </label>
            {d.hashtags?.length > 0 && <p className="marketing-source-kw">{d.hashtags.map((h) => `#${h}`).join(' ')}</p>}
            {graphicMeta?.image_prompt && (
              <p className="marketing-source-meta" title={graphicMeta.image_prompt}>
                Graphic prompt: {graphicMeta.image_prompt.slice(0, 120)}{graphicMeta.image_prompt.length > 120 ? '…' : ''}
              </p>
            )}
            {videoMeta?.video_prompt && (
              <p className="marketing-source-meta" title={videoMeta.video_prompt}>
                Video prompt: {videoMeta.video_prompt.slice(0, 120)}{videoMeta.video_prompt.length > 120 ? '…' : ''}
              </p>
            )}
            <MarketingDraftFeedback draftId={d.id} compact />
            <div className="marketing-form-actions">
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={busy || !edits[d.id]} onClick={() => saveEdit(d)}>SAVE EDITS</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={busy} onClick={() => reviewAction(d.id, 'approve')}>APPROVE</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" disabled={busy} onClick={() => reviewAction(d.id, 'reject')}>REJECT</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={busy} onClick={() => setGraphicPromptDraft(d)}>GRAPHIC</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={busy} onClick={() => setVideoPromptDraft(d)}>VIDEO</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={busy} onClick={() => setPreviewDraft(d)}>PREVIEW</button>
            </div>
          </>
        ) : (
          <>
            <strong>{d.title}</strong>
            <p className="marketing-source-topic">{(d.body || '').slice(0, 180)}{(d.body || '').length > 180 ? '…' : ''}</p>
            <div className="marketing-form-actions">
              {(d.status === 'approved' || d.status === 'scheduled') && (
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={busy} onClick={() => setPublishComposerDraft(d)}>
                  {d.status === 'scheduled' ? 'EDIT / PUBLISH' : 'PUBLISH'}
                </button>
              )}
              {d.status === 'approved' && (
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={busy} onClick={() => generateMedia(d.id, 'carousel')}>CAROUSEL</button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ STUDIO + REVIEW</h3>
      <p className="marketing-panel-desc">Write platform-native drafts from a trend, review &amp; edit them, then approve and publish via Postiz.</p>

      <div className="marketing-filter-bar">
        <span className="users-filter-tag">IDENTITY</span>
        <select value={selectedIdentity} onChange={(e) => onSelectIdentity(e.target.value)}>
          <option value="">All identities</option>
          {identities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <label>Trend / narrative
          <select value={selectedNarrative} onChange={(e) => setSelectedNarrative(e.target.value)}>
            <option value="">Select a trend…</option>
            {narratives.map((n) => (
              <option key={n.id} value={n.id}>[{n.pattern_strength ?? 0}] {n.title} ({n.brand_name || 'identity'})</option>
            ))}
          </select>
        </label>
        <p className="marketing-source-meta">Target channels (required — one draft per channel):</p>
        <div className="marketing-channel-checks">
          {channels.map((ch) => (
            <label key={ch.id} className="marketing-check-label">
              <input type="checkbox" checked={selectedChannels.includes(ch.id)} onChange={() => toggleChannel(ch.id)} />
              {ch.display_name || `${ch.brand_name} · ${ch.platform}`}
            </label>
          ))}
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={busy} onClick={generateDrafts}>
            {busy ? 'WORKING…' : 'GENERATE DRAFTS (OLLAMA)'}
          </button>
        </div>
        {msg && <p className={msg.toLowerCase().includes('fail') || msg.includes('Select') ? 'marketing-msg-err' : 'marketing-msg'}>{msg}</p>}
      </div>

      <h3 className="opo-section-heading">▮ REVIEW QUEUE<span className="blog-tactical-list-count opo-mono-value">{reviewQueue.length}</span></h3>
      {reviewQueue.length === 0 && <div className="blog-tactical-empty">NOTHING TO REVIEW — GENERATE DRAFTS FROM A TREND ABOVE</div>}
      <div className="marketing-channel-grid">{reviewQueue.map((d) => draftCard(d, true))}</div>

      <h3 className="opo-section-heading" style={{ marginTop: 20 }}>▮ APPROVED / SCHEDULED<span className="blog-tactical-list-count opo-mono-value">{approved.length}</span></h3>
      {approved.length === 0 && <div className="blog-tactical-empty">NO APPROVED DRAFTS YET</div>}
      <div className="marketing-channel-grid">{approved.map((d) => draftCard(d, false))}</div>

      {done.length > 0 && (
        <>
          <h3 className="opo-section-heading" style={{ marginTop: 20 }}>▮ HISTORY<span className="blog-tactical-list-count opo-mono-value">{done.length}</span></h3>
          <div className="marketing-channel-grid">{done.slice(0, 12).map((d) => draftCard(d, false))}</div>
        </>
      )}

      <h3 className="opo-section-heading" style={{ marginTop: 24 }}>▮ ASSET LIBRARY</h3>
      <AssetsPanel onPreviewAsset={onPreviewAsset} />

      {previewDraft && (
        <MarketingPlatformPreview draft={previewDraft} onClose={() => setPreviewDraft(null)} />
      )}
      {graphicPromptDraft && (
        <MarketingGraphicPromptModal
          draft={graphicPromptDraft}
          onClose={() => setGraphicPromptDraft(null)}
          onGenerated={async (data) => {
            const engine = data.engine ? ` (${data.engine})` : '';
            const mode = data.preview_mode ? ` · ${data.preview_mode}` : '';
            setMsg(`Image generated${engine}${mode}`);
            await load();
          }}
        />
      )}
      {videoPromptDraft && (
        <MarketingVideoPromptModal
          draft={videoPromptDraft}
          onClose={() => setVideoPromptDraft(null)}
          onGenerated={async (data) => {
            const engine = data.engine ? ` (${data.engine})` : '';
            const dur = data.duration_sec ? ` · ${data.duration_sec}s` : '';
            setMsg(`Video generated${engine}${dur}`);
            await load();
          }}
        />
      )}
      {publishComposerDraft && (
        <MarketingPublishModal
          draft={publishComposerDraft}
          channels={channels}
          onClose={() => { setPublishComposerDraft(null); load(); }}
          onChanged={load}
        />
      )}
    </div>
  );
}

function AnalyticsPanel({ onSync, syncBusy: parentSyncBusy }) {
  const [data, setData] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [panelSyncBusy, setPanelSyncBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    return mktFetch(`${API}/platform/analytics`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePanelSync = async () => {
    if (panelSyncBusy || parentSyncBusy) return;
    setPanelSyncBusy(true);
    try {
      await load();
      await onSync?.();
    } finally {
      setPanelSyncBusy(false);
    }
  };

  const syncBusy = panelSyncBusy || parentSyncBusy;

  const runOptimizer = async () => {
    setOptimizing(true);
    setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/analytics/optimize`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Optimize failed');
      setMsg(d.message || 'Optimization complete.');
      load();
      onSync?.();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setOptimizing(false);
    }
  };

  const channels = data?.channels || [];
  const insights = data?.insights || [];
  const identityGoals = data?.identity_goals || [];

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ ANALYTICS &amp; LEARNING</h3>
      <p className="marketing-panel-desc">Cross-channel performance, per-identity goal progress, and self-improving content patterns fed back into the writer.</p>

      {identityGoals.length > 0 && (
        <>
          <h3 className="opo-section-heading">▮ GOAL PROGRESS BY IDENTITY</h3>
          <ul className="marketing-narrative-list">
            {identityGoals.map((g) => (
              <li key={g.identity_id} className="marketing-narrative-item">
                <strong>{g.identity_name}</strong>
                <p>{g.pattern_value}</p>
                <span className="marketing-source-meta">{g.recommendation}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="marketing-form-actions">
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={optimizing} onClick={runOptimizer}>
          {optimizing ? 'ANALYZING…' : 'RUN ML OPTIMIZER'}
        </button>
        <MarketingSyncButton busy={syncBusy} onClick={handlePanelSync} />
      </div>
      {msg && <p className="marketing-msg">{msg}</p>}

      <div className="marketing-channel-grid">
        {channels.map((ch) => {
          const score = ch.goal_score != null ? Math.round(ch.goal_score) : null;
          const level = score == null ? 'unknown' : score >= 80 ? 'good' : score >= 50 ? 'warning' : 'critical';
          return (
            <div key={ch.id} className={`opo-card-tactical opo-card-${level}`}>
              <div className="opo-stat-header">
                <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
                <span className="opo-service-name">{ch.display_name || ch.platform}</span>
              </div>
              <p className="marketing-source-meta">{ch.brand_name}</p>
              <div className={`opo-kpi-value opo-mono-value opo-level-${level}`}>{score != null ? `${score}%` : '—'} goal</div>
              <div className="opo-bar-track opo-bar-mini">
                <div className={`opo-bar-fill opo-fill-${level}`} style={{ width: `${score || 0}%` }} />
              </div>
              <p className="marketing-source-meta">Posts: {ch.post_count ?? 0} · Avg engagement: {ch.avg_engagement != null ? ch.avg_engagement.toFixed(1) : '—'}</p>
            </div>
          );
        })}
      </div>

      <h3 className="opo-section-heading" style={{ marginTop: 20 }}>▮ LEARNED INSIGHTS</h3>
      {insights.length === 0 && <div className="blog-tactical-empty">RUN OPTIMIZER AFTER POSTS HAVE METRICS</div>}
      <ul className="marketing-narrative-list">
        {insights.map((ins, i) => (
          <li key={i} className="marketing-narrative-item">
            <strong>{ins.pattern_type}</strong>
            <p>{ins.recommendation}</p>
            <span className="marketing-source-meta">Confidence: {ins.confidence != null ? `${Math.round(ins.confidence * 100)}%` : '—'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const GOAL_METRIC_META = {
  engagement: { label: 'Engagement', unit: 'interactions', example: '100 = ~100 likes+comments+shares combined per post' },
  reach: { label: 'Reach', unit: 'views', example: '100 = ~100 views/impressions per post' },
  clicks: { label: 'Clicks', unit: 'clicks', example: '100 = ~100 link clicks per post' },
  leads: { label: 'Leads', unit: 'leads', example: '100 = ~100 form fills or DMs per post' },
  followers: { label: 'Followers', unit: 'new followers', example: '100 = ~100 net new followers per post' },
};

function IdentityLogoField({ identityId, logoUrl, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [hasLogo, setHasLogo] = useState(!!logoUrl);
  const [cacheBust, setCacheBust] = useState(() => Date.now());
  const fileRef = useRef(null);

  useEffect(() => {
    setHasLogo(!!logoUrl);
  }, [logoUrl]);

  const previewSrc = hasLogo
    ? `${API}/platform/identities/${identityId}/logo/file?ts=${cacheBust}`
    : null;

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mktFetch(`${API}/platform/identities/${identityId}/logo`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setHasLogo(true);
      setCacheBust(Date.now());
      setMsg('Logo linked — graphics for this identity will include it.');
      onChanged?.();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!hasLogo || busy) return;
    if (!window.confirm('Remove the logo from this identity?')) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await mktFetch(`${API}/platform/identities/${identityId}/logo`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Remove failed');
      setHasLogo(false);
      setMsg('Logo removed.');
      onChanged?.();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="marketing-identity-logo-field marketing-form-full">
      <span className="marketing-ruleset-section-label">Brand logo</span>
      <p className="marketing-field-hint marketing-logo-hint">
        Optional. PNG with a transparent background works best. When linked, it is composited in the bottom-right corner of generated graphics for content under this identity.
      </p>
      <div className="marketing-logo-row">
        {previewSrc ? (
          <div className="marketing-logo-preview">
            <img src={previewSrc} alt="Identity logo preview" />
          </div>
        ) : (
          <div className="marketing-logo-preview marketing-logo-preview-empty">No logo</div>
        )}
        <div className="marketing-logo-actions">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            className="marketing-logo-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-edit"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? 'UPLOADING…' : (hasLogo ? 'REPLACE LOGO' : 'UPLOAD LOGO')}
          </button>
          {hasLogo && (
            <button
              type="button"
              className="blog-tactical-action-btn blog-tactical-action-delete"
              disabled={busy}
              onClick={remove}
            >
              REMOVE
            </button>
          )}
        </div>
      </div>
      {msg && <p className="marketing-msg">{msg}</p>}
    </div>
  );
}

function RulesetEditor({ identityId, identityLogoUrl, onIdentityChanged, voiceProfiles, compliancePresets }) {
  const [ruleset, setRuleset] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    mktFetch(`${API}/platform/rulesets?identity_id=${identityId}`)
      .then((r) => r.json())
      .then((d) => setRuleset((d.rulesets || [])[0] || null))
      .catch(() => {});
  }, [identityId]);

  useEffect(() => { load(); }, [load]);

  if (!ruleset) return <p className="marketing-source-meta">No rule-set yet.</p>;

  const set = (k, v) => setRuleset((p) => ({ ...p, [k]: v }));
  const goalMeta = GOAL_METRIC_META[ruleset.goal_metric || 'engagement'] || GOAL_METRIC_META.engagement;
  const selectedVoice = voiceProfiles.find((v) => v.id === ruleset.voice_profile_id);

  const onVoiceChange = (voiceId) => {
    const id = voiceId ? parseInt(voiceId, 10) : null;
    const vp = voiceProfiles.find((v) => v.id === id);
    setRuleset((p) => ({ ...p, voice_profile_id: id, tone: vp?.tone || p.tone }));
  };

  const onComplianceChange = (presetId) => {
    const id = presetId ? parseInt(presetId, 10) : null;
    const preset = compliancePresets.find((p) => p.id === id);
    setRuleset((r) => ({
      ...r,
      compliance_preset_id: id,
      compliance_notes: preset?.body || r.compliance_notes,
    }));
  };

  const save = async () => {
    setMsg('');
    const res = await mktFetch(`${API}/platform/rulesets/${ruleset.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        voice_profile_id: ruleset.voice_profile_id || null,
        compliance_preset_id: ruleset.compliance_preset_id || null,
        tone: ruleset.tone,
        min_length: ruleset.min_length ? Number(ruleset.min_length) : null,
        max_length: ruleset.max_length ? Number(ruleset.max_length) : null,
        hashtag_min: Number(ruleset.hashtag_min || 0),
        hashtag_max: Number(ruleset.hashtag_max || 5),
        cta_style: ruleset.cta_style,
        compliance_notes: ruleset.compliance_notes,
        posts_per_week: Number(ruleset.posts_per_week || 3),
        approval_mode: ruleset.approval_mode,
        goal_metric: ruleset.goal_metric,
        goal_target: Number(ruleset.goal_target || 100),
        banned_topics: ruleset.banned_topics,
        banned_words: ruleset.banned_words,
      }),
    });
    setMsg(res.ok ? 'Rule-set saved.' : 'Save failed.');
    load();
  };

  return (
    <div className="marketing-ruleset-grid">
      <IdentityLogoField
        identityId={identityId}
        logoUrl={identityLogoUrl}
        onChanged={onIdentityChanged}
      />
      <label>Voice / tone
        <select value={ruleset.voice_profile_id || ''} onChange={(e) => onVoiceChange(e.target.value)}>
          <option value="">Select a voice profile…</option>
          {voiceProfiles.map((v) => (
            <option key={v.id} value={v.id}>{v.name}{v.tone ? ` — ${v.tone}` : ''}</option>
          ))}
        </select>
        {selectedVoice?.style_notes && (
          <span className="marketing-field-hint">{selectedVoice.style_notes}</span>
        )}
        {!voiceProfiles.length && (
          <span className="marketing-field-hint">Create voice profiles in the Voice Profiles tab, then link one when adding a channel.</span>
        )}
      </label>
      <label>Approval mode
        <select value={ruleset.approval_mode || 'review'} onChange={(e) => set('approval_mode', e.target.value)}>
          <option value="review">Human review</option>
          <option value="auto">Auto-approve</option>
        </select>
      </label>
      <label>Min paragraphs
        <input type="number" min={1} max={20} value={ruleset.min_length || ''} onChange={(e) => set('min_length', e.target.value)} placeholder="e.g. 2" />
        <span className="marketing-field-hint">Minimum paragraphs in the post body (leave empty for no minimum).</span>
      </label>
      <label>Max paragraphs
        <input type="number" min={1} max={20} value={ruleset.max_length || ''} onChange={(e) => set('max_length', e.target.value)} placeholder="e.g. 3" />
        <span className="marketing-field-hint">Maximum paragraphs in the post body.</span>
      </label>
      <label>Posts / week
        <input type="number" value={ruleset.posts_per_week || ''} onChange={(e) => set('posts_per_week', e.target.value)} />
      </label>
      <label>Hashtags min<input type="number" value={ruleset.hashtag_min ?? 0} onChange={(e) => set('hashtag_min', e.target.value)} /></label>
      <label>Hashtags max<input type="number" value={ruleset.hashtag_max ?? 5} onChange={(e) => set('hashtag_max', e.target.value)} /></label>
      <label>CTA style<input value={ruleset.cta_style || ''} onChange={(e) => set('cta_style', e.target.value)} placeholder="Soft, link in bio" /></label>
      <label>Goal metric
        <select value={ruleset.goal_metric || 'engagement'} onChange={(e) => set('goal_metric', e.target.value)}>
          {Object.entries(GOAL_METRIC_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
      </label>
      <label>Goal target ({goalMeta.unit})
        <input type="number" min={1} value={ruleset.goal_target || ''} onChange={(e) => set('goal_target', e.target.value)} placeholder="100" />
        <span className="marketing-field-hint">{goalMeta.example}</span>
      </label>
      <label className="marketing-form-full">Banned topics (comma-separated)
        <input value={(ruleset.banned_topics || []).join(', ')} onChange={(e) => set('banned_topics', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />
      </label>
      <label className="marketing-form-full">Banned words (comma-separated)
        <input value={(ruleset.banned_words || []).join(', ')} onChange={(e) => set('banned_words', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />
      </label>
      <label className="marketing-form-full">Compliance preset
        <select value={ruleset.compliance_preset_id || ''} onChange={(e) => onComplianceChange(e.target.value)}>
          <option value="">Select compliance notes…</option>
          {compliancePresets.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {ruleset.compliance_notes && (
          <span className="marketing-field-hint marketing-compliance-preview">{ruleset.compliance_notes}</span>
        )}
        {!compliancePresets.length && (
          <span className="marketing-field-hint">Add presets in the Compliance Library tab, then pick one here.</span>
        )}
      </label>
      <div className="marketing-form-actions marketing-form-full">
        <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={save}>SAVE RULE-SET</button>
        {msg && <span className="marketing-msg">{msg}</span>}
      </div>
    </div>
  );
}

function ComplianceLibraryPanel({ onChanged }) {
  const [presets, setPresets] = useState([]);

  const load = useCallback(() => {
    mktFetch(`${API}/platform/compliance-presets`)
      .then((r) => r.json())
      .then((c) => setPresets(c.presets || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChanged = () => {
    load();
    onChanged?.();
  };

  return <CompliancePresetsPanel presets={presets} onChanged={handleChanged} />;
}

function VoiceProfilesPanel({ onChanged }) {
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [msg, setMsg] = useState('');
  const [vpForm, setVpForm] = useState({ name: '', tone: '', style_notes: '', do_rules: '', dont_rules: '' });

  const load = useCallback(() => {
    mktFetch(`${API}/platform/voice-profiles`)
      .then((r) => r.json())
      .then((v) => setVoiceProfiles(v.voice_profiles || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const createVoice = async () => {
    setMsg('');
    if (!vpForm.name) { setMsg('Voice name required.'); return; }
    const res = await mktFetch(`${API}/platform/voice-profiles`, { method: 'POST', body: JSON.stringify(vpForm) });
    const d = await res.json();
    if (!res.ok) { setMsg(d.error || 'Create failed'); return; }
    setVpForm({ name: '', tone: '', style_notes: '', do_rules: '', dont_rules: '' });
    setMsg('Voice profile added — link it when creating a channel.');
    load();
    onChanged?.();
  };

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ VOICE PROFILES<span className="blog-tactical-list-count opo-mono-value">{voiceProfiles.length}</span></h3>
      <p className="marketing-panel-desc">Reusable voices you attach to channels — tone, style, and do/don&apos;t rules the writer follows. Create voices here first, then pick one when adding a channel.</p>
      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="opo-stat-header"><span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span><span className="opo-service-name">NEW VOICE PROFILE</span></div>
        <div className="marketing-form-grid">
          <label>Name<input value={vpForm.name} onChange={(e) => setVpForm((v) => ({ ...v, name: e.target.value }))} placeholder="e.g. Frederik — founder voice" /></label>
          <label>Tone<input value={vpForm.tone} onChange={(e) => setVpForm((v) => ({ ...v, tone: e.target.value }))} placeholder="Warm, expert" /></label>
          <label className="marketing-form-full">Style notes<input value={vpForm.style_notes} onChange={(e) => setVpForm((v) => ({ ...v, style_notes: e.target.value }))} /></label>
          <label>Always (do)<input value={vpForm.do_rules} onChange={(e) => setVpForm((v) => ({ ...v, do_rules: e.target.value }))} /></label>
          <label>Never (don&apos;t)<input value={vpForm.dont_rules} onChange={(e) => setVpForm((v) => ({ ...v, dont_rules: e.target.value }))} /></label>
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={createVoice}>ADD VOICE PROFILE</button>
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>
      <div className="marketing-source-list">
        {voiceProfiles.length === 0 && (
          <div className="blog-tactical-empty">NO VOICE PROFILES YET — ADD ONE ABOVE, THEN LINK IT ON THE CHANNELS TAB</div>
        )}
        {voiceProfiles.map((v) => (
          <div key={v.id} className="opo-card-tactical opo-card-good marketing-source-card">
            <div className="opo-stat-header"><span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span><span className="opo-service-name">{v.name}</span></div>
            {v.tone && <p className="marketing-source-topic">{v.tone}</p>}
            {v.style_notes && <p className="marketing-source-meta">{v.style_notes}</p>}
            {(v.do_rules || v.dont_rules) && (
              <p className="marketing-source-meta">
                {v.do_rules ? `Do: ${v.do_rules}` : ''}{v.do_rules && v.dont_rules ? ' · ' : ''}{v.dont_rules ? `Don't: ${v.dont_rules}` : ''}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompliancePresetsPanel({ presets, onChanged }) {
  const [form, setForm] = useState({ name: '', body: '' });
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState('');

  const save = async () => {
    if (!form.name || !form.body) { setMsg('Name and body required.'); return; }
    setMsg('');
    const url = editId ? `${API}/platform/compliance-presets/${editId}` : `${API}/platform/compliance-presets`;
    const res = await mktFetch(url, {
      method: editId ? 'PATCH' : 'POST',
      body: JSON.stringify(form),
    });
    if (!res.ok) { setMsg('Save failed.'); return; }
    setForm({ name: '', body: '' });
    setEditId(null);
    setMsg(editId ? 'Preset updated.' : 'Preset added.');
    onChanged?.();
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, body: p.body });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this compliance preset?')) return;
    await mktFetch(`${API}/platform/compliance-presets/${id}`, { method: 'DELETE' });
    onChanged?.();
  };

  return (
    <div className="marketing-tactical-panel marketing-compliance-panel">
      <h3 className="opo-section-heading">▮ COMPLIANCE LIBRARY<span className="blog-tactical-list-count opo-mono-value">{presets.length}</span></h3>
      <p className="marketing-panel-desc">Reusable compliance notes for rule-sets. Add RESA, PIPA, or industry-specific disclaimers once, then pick them from any identity&apos;s rule-set.</p>
      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="marketing-form-grid">
          <label>Name<input value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))} placeholder="e.g. RESA (BC Real Estate)" /></label>
          <label className="marketing-form-full">Compliance text
            <textarea rows={3} value={form.body} onChange={(e) => setForm((v) => ({ ...v, body: e.target.value }))} placeholder="Full compliance instructions for the writer…" />
          </label>
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={save}>{editId ? 'UPDATE PRESET' : 'ADD PRESET'}</button>
          {editId && (
            <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => { setEditId(null); setForm({ name: '', body: '' }); }}>CANCEL</button>
          )}
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>
      <div className="marketing-source-list">
        {presets.length === 0 && (
          <div className="blog-tactical-empty">NO PRESETS YET — ADD RESA, PIPA, OR INDUSTRY RULES ABOVE, THEN LINK THEM IN IDENTITIES → EDIT RULE-SET</div>
        )}
        {presets.map((p) => (
          <div key={p.id} className="opo-card-tactical opo-card-good marketing-source-card">
            <div className="opo-stat-header">
              <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
              <span className="opo-service-name">{p.name}</span>
            </div>
            <p className="marketing-source-topic">{p.body}</p>
            <div className="marketing-form-actions">
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => startEdit(p)}>EDIT</button>
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={() => remove(p.id)}>DELETE</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IdentitiesPanel({ onChanged }) {
  const [identities, setIdentities] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [compliancePresets, setCompliancePresets] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState('');
  const [logoEpoch, setLogoEpoch] = useState(0);
  const [form, setForm] = useState({ name: '', audience: '', goal_target: '' });

  const load = useCallback(() => {
    Promise.all([
      mktFetch(`${API}/platform/identities`).then((r) => r.json()),
      mktFetch(`${API}/platform/voice-profiles`).then((r) => r.json()),
      mktFetch(`${API}/platform/compliance-presets`).then((r) => r.json()),
    ]).then(([i, v, c]) => {
      setIdentities(i.identities || []);
      setVoiceProfiles(v.voice_profiles || []);
      setCompliancePresets(c.presets || []);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const createIdentity = async () => {
    setMsg('');
    if (!form.name) { setMsg('Name required.'); return; }
    const res = await mktFetch(`${API}/platform/identities`, {
      method: 'POST',
      body: JSON.stringify({
        name: form.name, audience: form.audience || null,
        goals: form.goal_target ? { engagement_target: Number(form.goal_target) } : null,
      }),
    });
    const d = await res.json();
    if (!res.ok) { setMsg(d.error || 'Create failed'); return; }
    setForm({ name: '', audience: '', goal_target: '' });
    setMsg('Identity created with a default rule-set.');
    load(); onChanged?.();
  };

  const handleIdentityChanged = () => {
    setLogoEpoch(Date.now());
    load();
    onChanged?.();
  };

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ IDENTITIES</h3>
      <p className="marketing-panel-desc">
        An identity is a brand you create content for — audience, goals, and rule-set. Each identity owns its sources and channels.
        Add compliance presets in <strong>Compliance Library</strong>, then link them via <strong>Edit rule-set</strong> below.
      </p>

      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="opo-stat-header"><span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span><span className="opo-service-name">NEW IDENTITY</span></div>
        <div className="marketing-form-grid">
          <label>Name<input value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))} placeholder="e.g. Acme Plumbing" /></label>
          <label>Audience<input value={form.audience} onChange={(e) => setForm((v) => ({ ...v, audience: e.target.value }))} placeholder="Who you're talking to" /></label>
          <label>Engagement goal<input type="number" value={form.goal_target} onChange={(e) => setForm((v) => ({ ...v, goal_target: e.target.value }))} placeholder="e.g. 100" /></label>
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={createIdentity}>CREATE IDENTITY</button>
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>

      <div className="marketing-source-list marketing-identity-list">
        {identities.map((b) => (
          <div key={b.id} className={`opo-card-tactical opo-card-good marketing-source-card marketing-identity-card${expanded === b.id ? ' is-expanded' : ''}`}>
            <div className="opo-stat-header">
              <div className="marketing-identity-title-row">
                <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                {b.logo_url && (
                  <img
                    src={`${API}/platform/identities/${b.id}/logo/file?v=${logoEpoch || b.logo_url}`}
                    alt=""
                    className="marketing-identity-logo-icon"
                  />
                )}
                <span className="opo-service-name">{b.name}</span>
              </div>
              <span className="widgets-status-badge widgets-status-good">{b.approval_mode || 'review'}</span>
            </div>
            {b.audience && <p className="marketing-source-topic">{b.audience}</p>}
            <p className="marketing-source-meta">{b.source_count ?? 0} sources · {b.channel_count ?? 0} channels · goal {b.goal_metric || 'engagement'} {b.goal_target || ''}</p>
            <div className="marketing-form-actions">
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>
                {expanded === b.id ? 'HIDE RULE-SET' : 'EDIT RULE-SET'}
              </button>
            </div>
            {expanded === b.id && (
              <RulesetEditor
                identityId={b.id}
                identityLogoUrl={b.logo_url}
                onIdentityChanged={handleIdentityChanged}
                voiceProfiles={voiceProfiles}
                compliancePresets={compliancePresets}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendsPanel({ identities, selectedIdentity, onSelectIdentity, onNavigate, onChanged }) {
  const [narratives, setNarratives] = useState([]);
  const [channels, setChannels] = useState([]);
  const [writeTarget, setWriteTarget] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const idQ = selectedIdentity ? `&identity_id=${selectedIdentity}` : '';
  const channelQ = selectedIdentity ? `?identity_id=${selectedIdentity}` : '';
  const load = useCallback(() => {
    return Promise.all([
      mktFetch(`${API}/platform/narratives?limit=40${idQ}`).then((r) => r.json()),
      mktFetch(`${API}/platform/channels${channelQ}`).then((r) => r.json()),
    ]).then(([n, c]) => {
      setNarratives(n.narratives || []);
      setChannels(c.channels || []);
    }).catch(() => {});
  }, [idQ, channelQ]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    if (syncBusy) return;
    setSyncBusy(true);
    try {
      await load();
      await onChanged?.();
    } finally {
      setSyncBusy(false);
    }
  };

  const dismissTrend = async (n) => {
    if (writeTarget) return;
    const ok = await dismissNarrative(n.id);
    if (ok) {
      setMsg(`Removed "${n.title}".`);
      load();
      onChanged?.();
    }
  };

  const maxStrength = Math.max(1, ...narratives.map((n) => n.pattern_strength || 0));

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ TRENDS &amp; PATTERNS</h3>
      <p className="marketing-panel-desc">
        Editorial scout findings for your sources, ranked by pattern strength. Filter by identity, then click <strong>Write content</strong> to choose
        which channels (LinkedIn, TikTok, OPO NEWS, etc.) get a draft. Each trend is written once — it then moves to Studio + Review.
      </p>

      <div className="marketing-filter-bar">
        <span className="users-filter-tag">IDENTITY</span>
        <select value={selectedIdentity} onChange={(e) => onSelectIdentity(e.target.value)}>
          <option value="">All identities</option>
          {identities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <MarketingSyncButton busy={syncBusy} onClick={handleSync} />
      </div>
      {msg && <p className="marketing-msg">{msg}</p>}

      {narratives.length === 0 && <div className="blog-tactical-empty">NO TRENDS YET — ADD SOURCES, THEN RUN THE SCOUT (OVERVIEW)</div>}
      <div className="marketing-source-list">
        {narratives.map((n) => {
          const pct = Math.round(((n.pattern_strength || 0) / maxStrength) * 100);
          const level = pct >= 66 ? 'good' : pct >= 33 ? 'warning' : 'unknown';
          return (
            <div key={n.id} className={`opo-card-tactical opo-card-${level} marketing-source-card`}>
              <div className="opo-stat-header">
                <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
                <span className="opo-service-name">{n.title}</span>
                <span className={`widgets-status-badge widgets-status-${level}`}>STRENGTH {n.pattern_strength ?? 0}</span>
              </div>
              <p className="marketing-source-topic">{n.summary}</p>
              <div className="opo-bar-track opo-bar-mini"><div className={`opo-bar-fill opo-fill-${level}`} style={{ width: `${pct}%` }} /></div>
              <p className="marketing-source-meta">{n.brand_name} · {n.narrative_date ? new Date(n.narrative_date).toLocaleDateString() : ''}</p>
              <div className="marketing-form-actions marketing-trend-actions">
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={!!writeTarget} onClick={() => setWriteTarget(n)}>
                  WRITE CONTENT
                </button>
                <button type="button" className="marketing-trend-dismiss" title="Dismiss trend" aria-label="Dismiss trend" disabled={!!writeTarget} onClick={() => dismissTrend(n)}>
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {writeTarget && (
        <WriteContentModal
          narrative={writeTarget}
          channels={channels}
          onClose={() => setWriteTarget(null)}
          onDone={async () => {
            await load();
            onChanged?.();
          }}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

function SchedulesPanel({ identities }) {
  const [schedules, setSchedules] = useState([]);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ identity_id: '', action: 'read', cadence: 'daily', run_at_hour: 8, run_at_minute: 0, interval_minutes: 60 });

  const load = useCallback(() => {
    mktFetch(`${API}/platform/schedules`).then((r) => r.json()).then((d) => setSchedules(d.schedules || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setMsg('');
    if (!form.identity_id) { setMsg('Select an identity.'); return; }
    const res = await mktFetch(`${API}/platform/schedules`, { method: 'POST', body: JSON.stringify(form) });
    const d = await res.json();
    if (!res.ok) { setMsg(d.error || 'Create failed'); return; }
    setMsg('Schedule created.');
    load();
  };

  const toggle = async (s) => { await mktFetch(`${API}/platform/schedules/${s.id}`, { method: 'PATCH', body: JSON.stringify({ active: !s.active }) }); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this schedule?')) return; await mktFetch(`${API}/platform/schedules/${id}`, { method: 'DELETE' }); load(); };

  const actionLabel = { read: 'Read sources', generate: 'Generate drafts', publish: 'Publish approved' };

  return (
    <div className="marketing-tactical-panel">
      <h3 className="opo-section-heading">▮ SCHEDULES</h3>
      <p className="marketing-panel-desc">Define when the engine runs per identity. The scheduler ticks every ~5 minutes and runs due actions: read sources, generate drafts, or publish approved content.</p>

      <div className="opo-card-tactical opo-card-good marketing-form-card">
        <div className="marketing-form-grid">
          <label>Identity
            <select value={form.identity_id} onChange={(e) => setForm((v) => ({ ...v, identity_id: e.target.value }))}>
              <option value="">Select identity</option>
              {identities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label>Action
            <select value={form.action} onChange={(e) => setForm((v) => ({ ...v, action: e.target.value }))}>
              <option value="read">Read sources</option>
              <option value="generate">Generate drafts</option>
              <option value="publish">Publish approved</option>
            </select>
          </label>
          <label>Cadence
            <select value={form.cadence} onChange={(e) => setForm((v) => ({ ...v, cadence: e.target.value }))}>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="interval">Every N minutes</option>
            </select>
          </label>
          {form.cadence === 'interval' ? (
            <label>Interval (min)<input type="number" value={form.interval_minutes} onChange={(e) => setForm((v) => ({ ...v, interval_minutes: Number(e.target.value) }))} /></label>
          ) : (
            <>
              <label>Hour (0-23)<input type="number" value={form.run_at_hour} onChange={(e) => setForm((v) => ({ ...v, run_at_hour: Number(e.target.value) }))} /></label>
              <label>Minute<input type="number" value={form.run_at_minute} onChange={(e) => setForm((v) => ({ ...v, run_at_minute: Number(e.target.value) }))} /></label>
            </>
          )}
        </div>
        <div className="marketing-form-actions">
          <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" onClick={create}>CREATE SCHEDULE</button>
        </div>
        {msg && <p className="marketing-msg">{msg}</p>}
      </div>

      {schedules.length === 0 && <div className="blog-tactical-empty">NO SCHEDULES — CONTENT RUNS ONLY WHEN TRIGGERED MANUALLY</div>}
      <div className="marketing-source-list">
        {schedules.map((s) => {
          const level = s.active ? 'good' : 'unknown';
          return (
            <div key={s.id} className={`opo-card-tactical opo-card-${level} marketing-source-card`}>
              <div className="opo-stat-header">
                <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
                <span className="opo-service-name">{s.identity_name} · {actionLabel[s.action] || s.action}</span>
                <span className={`widgets-status-badge widgets-status-${level}`}>{s.active ? 'ACTIVE' : 'PAUSED'}</span>
              </div>
              <p className="marketing-source-meta">
                {s.cadence}{s.cadence === 'interval' ? ` (${s.interval_minutes}m)` : ` @ ${String(s.run_at_hour).padStart(2, '0')}:${String(s.run_at_minute).padStart(2, '0')}`}
                {s.next_run_at ? ` · next ${new Date(s.next_run_at).toLocaleString()}` : ''}
                {s.last_run_at ? ` · last ${new Date(s.last_run_at).toLocaleString()}` : ''}
              </p>
              <div className="marketing-form-actions">
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" onClick={() => toggle(s)}>{s.active ? 'PAUSE' : 'RESUME'}</button>
                <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={() => remove(s.id)}>DELETE</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MarketingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialCat = searchParams.get('category') || 'overview';
  const [category, setCategoryState] = useState(initialCat);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [overview, setOverview] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedIdentity, setSelectedIdentity] = useState('');
  const [scoutBusy, setScoutBusy] = useState(false);
  const [scoutMsg, setScoutMsg] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncTs, setSyncTs] = useState(Date.now());

  const loadOverview = useCallback(async () => {
    const [ov, br] = await Promise.all([
      mktFetch(`${API}/platform/overview`).then((r) => r.json()),
      mktFetch(`${API}/platform/brands`).then((r) => r.json()),
    ]);
    setOverview(ov);
    setBrands(br.brands || []);
    setSyncTs(Date.now());
  }, []);

  const handleSync = useCallback(async () => {
    if (syncBusy) return;
    setSyncBusy(true);
    try {
      await loadOverview();
    } finally {
      setSyncBusy(false);
    }
  }, [syncBusy, loadOverview]);

  useEffect(() => { loadOverview().catch(() => {}); }, [loadOverview]);

  const setCategory = useCallback((id) => {
    setCategoryState(id);
    navigate(`/marketing?category=${id}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat && ALL_TABS.some((c) => c.id === cat)) setCategoryState(cat);
  }, [searchParams]);

  const activeGroup = ALL_TABS.find((t) => t.id === category)?.group || 'operations';

  const stats = overview?.stats || {};
  const lastRun = overview?.last_editorial_run;

  const heroLevel = useMemo(() => {
    if (lastRun?.status === 'failed') return 'critical';
    if ((stats.active_sources || 0) === 0) return 'critical';
    if (lastRun?.status === 'running') return 'warning';
    if ((stats.narratives_today || 0) === 0 && lastRun?.status !== 'completed') return 'warning';
    return 'good';
  }, [lastRun, stats]);

  const heroLabel = runLevelLabel(heroLevel);
  const narrativesPct = stats.narratives_today > 0 ? 100 : 0;
  const connectedPct = stats.channels > 0 ? Math.round(((stats.connected_channels || 0) / stats.channels) * 100) : 0;
  const sourcesLevel = (stats.active_sources || 0) > 0 ? 'good' : 'critical';

  const dismissNarrativeFromOverview = async (id) => {
    const ok = await dismissNarrative(id);
    if (ok) loadOverview();
  };

  const runEditorialScout = async () => {
    if (scoutBusy) return;
    if (!window.confirm(
      'Start editorial scout?\n\nThis reads all active source websites, finds trending topics with Ollama, and creates new narratives. Usually takes 1–3 minutes.\n\nOnly click once — wait for the running indicator to finish.'
    )) return;
    setScoutBusy(true);
    setScoutMsg('Starting editorial scout…');
    try {
      const res = await mktFetch(`${API}/platform/agents/editorial/run`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        setScoutMsg(data.error || 'Failed to start scout.');
        setScoutBusy(false);
        return;
      }
      setScoutMsg(data.message || `Scout run #${data.run_id} started.`);

      const targetRunId = data.run_id;
      for (let i = 0; i < 36; i++) {
        await new Promise((r) => setTimeout(r, 10000));
        const ov = await mktFetch(`${API}/platform/overview`).then((r) => r.json());
        setOverview(ov);
        setSyncTs(Date.now());
        const run = ov.last_editorial_run;
        if (run && run.id === targetRunId && run.status === 'completed') {
          const stats = typeof run.stats === 'string' ? JSON.parse(run.stats) : (run.stats || {});
          setScoutMsg(`Scout complete — ${stats.articlesIngested ?? 0} article(s), ${stats.narrativesCreated ?? 0} narrative(s). Open Trends to review.`);
          setScoutBusy(false);
          return;
        }
        if (run && run.id === targetRunId && run.status === 'failed') {
          setScoutMsg(`Scout failed: ${run.error_message || 'unknown error'}`);
          setScoutBusy(false);
          return;
        }
        if (run?.status === 'running') {
          setScoutMsg(`Scout running… ${(i + 1) * 10}s elapsed (usually finishes in 1–3 min)`);
        }
      }
      setScoutMsg('Scout is still running or took longer than expected — click SYNC to refresh.');
    } catch (e) {
      setScoutMsg(e.message || 'Scout error');
    } finally {
      setScoutBusy(false);
    }
  };

  const renderCategory = () => {
    switch (category) {
      case 'overview':
        return (
          <OverviewPanel
            overview={overview}
            onNavigate={setCategory}
            onSync={handleSync}
            syncBusy={syncBusy}
            scoutBusy={scoutBusy}
            scoutMsg={scoutMsg}
            onRunScout={runEditorialScout}
            onDismissNarrative={dismissNarrativeFromOverview}
          />
        );
      case 'identities':
        return <IdentitiesPanel onChanged={loadOverview} />;
      case 'compliance':
        return <ComplianceLibraryPanel onChanged={loadOverview} />;
      case 'voice-profiles':
        return <VoiceProfilesPanel onChanged={loadOverview} />;
      case 'sources':
        return <SourcesPanel brands={brands} onChanged={loadOverview} />;
      case 'trends':
        return (
          <TrendsPanel
            identities={brands}
            selectedIdentity={selectedIdentity}
            onSelectIdentity={setSelectedIdentity}
            onNavigate={setCategory}
            onChanged={loadOverview}
          />
        );
      case 'studio':
        return (
          <StudioReviewPanel
            onPreviewAsset={setPreviewAsset}
            identities={brands}
            selectedIdentity={selectedIdentity}
            onSelectIdentity={setSelectedIdentity}
          />
        );
      case 'schedules':
        return <SchedulesPanel identities={brands} />;
      case 'channels':
        return <ChannelsPanel postiz={overview?.postiz} onSync={handleSync} syncBusy={syncBusy} />;
      case 'analytics':
        return <AnalyticsPanel onSync={handleSync} syncBusy={syncBusy} />;
      case 'blog-management':
        return (
          <div className="blog-shell-content">
            <BlogManager />
          </div>
        );
      default:
        return (
          <OverviewPanel
            overview={overview}
            onNavigate={setCategory}
            onSync={handleSync}
            syncBusy={syncBusy}
            scoutBusy={scoutBusy}
            scoutMsg={scoutMsg}
            onRunScout={runEditorialScout}
            onDismissNarrative={dismissNarrativeFromOverview}
          />
        );
    }
  };

  return (
    <div className="card feedback-card opo-tactical blog-shell marketing-shell">
      <div className="card-header blog-shell-header">
        <h2 className="blog-shell-title">
          <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
          MARKETING OPERATIONS
        </h2>
        <div className="card-actions">
          <span className="blog-shell-tag">EDITORIAL · POSTIZ · OLLAMA</span>
        </div>
      </div>
      <div className="card-content blog-shell-content marketing-shell-content">
        <div className="marketing-tactical-top">
          <div className={`opo-hero opo-hero-${heroLevel}`}>
            <div className="opo-hero-scan" aria-hidden="true" />
            <div className="opo-hero-left">
              <div className="opo-hero-eyebrow">CONTENT COMMAND CENTER</div>
              <div className="opo-hero-title">
                <span className={`opo-hero-pulse opo-pulse-${heroLevel}`} />
                <h2>{heroLabel}</h2>
              </div>
              <div className="opo-hero-sub">
                {stats.active_sources ?? 0} active sources · {stats.narratives_today ?? 0} narratives today · {stats.connected_channels ?? 0}/{stats.channels ?? 0} channels connected
              </div>
            </div>
            <div className="opo-hero-right">
              <MarketingSyncButton busy={syncBusy} onClick={handleSync} />
              <MarketingScoutButton busy={scoutBusy} onClick={runEditorialScout} />
              {(scoutBusy || syncBusy) && (
                <span className="marketing-hero-status opo-mono-value">
                  {scoutBusy ? 'Scout processing…' : 'Refreshing…'}
                </span>
              )}
              <div className="opo-hero-timestamp">Updated {new Date(syncTs).toLocaleString()}</div>
            </div>
          </div>

          <div className="opo-kpi-row">
            <div className="opo-kpi">
              <div className="opo-kpi-label">NARRATIVES TODAY</div>
              <div className={`opo-kpi-value opo-mono-value opo-level-${stats.narratives_today > 0 ? 'good' : 'warning'}`}>
                {stats.narratives_today ?? 0}
              </div>
              <div className="opo-bar-track opo-bar-mini">
                <div className={`opo-bar-fill opo-fill-${stats.narratives_today > 0 ? 'good' : 'warning'}`} style={{ width: `${narrativesPct}%` }} />
              </div>
              <div className="opo-kpi-foot">from editorial scout</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">ACTIVE SOURCES</div>
              <div className={`opo-kpi-value opo-mono-value opo-level-${sourcesLevel}`}>{stats.active_sources ?? 0}</div>
              <div className="opo-kpi-foot">{stats.sources ?? 0} total configured</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">DRAFTS TODAY</div>
              <div className="opo-kpi-value opo-mono-value">{stats.drafts_today ?? 0}</div>
              <div className="opo-kpi-foot">{stats.scheduled_posts ?? 0} scheduled</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">CHANNELS CONNECTED</div>
              <div className={`opo-kpi-value opo-mono-value opo-level-${connectedPct >= 50 ? 'good' : connectedPct > 0 ? 'warning' : 'critical'}`}>
                {stats.connected_channels ?? 0}<span className="opo-kpi-sep">/</span>{stats.channels ?? 0}
              </div>
              <div className="opo-bar-track opo-bar-mini">
                <div className={`opo-bar-fill opo-fill-${connectedPct >= 50 ? 'good' : 'warning'}`} style={{ width: `${connectedPct}%` }} />
              </div>
              <div className="opo-kpi-foot">{connectedPct}% via Postiz</div>
            </div>
          </div>
        </div>

        <nav className={`marketing-tab-nav marketing-tab-nav--${activeGroup}`} aria-label="Marketing sections">
          {TAB_GROUPS.map((group, groupIndex) => (
            <div
              key={group.id}
              className={`marketing-tab-group marketing-tab-group--${group.id}${category && group.tabs.some((t) => t.id === category) ? ' is-current-group' : ''}`}
              role="group"
              aria-label={`${group.label}: ${group.hint}`}
            >
              <div className="marketing-tab-group-head">
                <span className={`marketing-tab-group-label marketing-tab-group-label--${group.id}`}>{group.label}</span>
                <span className="marketing-tab-group-hint">{group.hint}</span>
              </div>
              <div className={`widgets-tab-strip marketing-tab-strip marketing-tab-strip--${group.id}`}>
                {group.tabs.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`widgets-tab-btn marketing-tab-btn marketing-tab-btn--${group.id}${category === cat.id ? ' is-active' : ''}`}
                    onClick={() => setCategory(cat.id)}
                  >
                    <i className={cat.icon} aria-hidden />
                    {cat.label}
                  </button>
                ))}
              </div>
              {groupIndex < TAB_GROUPS.length - 1 && (
                <div className="marketing-tab-group-separator" role="separator" aria-hidden="true" />
              )}
            </div>
          ))}
        </nav>

        <div className="marketing-page-body">
          {renderCategory()}
        </div>
      </div>
      {previewAsset && (
        <AssetPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      )}
    </div>
  );
}
